import React, { useState } from 'react';
import { Check, X, Trash2, Crown, UserPlus, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import PositionSelector from './PositionSelector';

// Fixed row height to align with the family labels column
const FAMILY_ROW_HEIGHT = 'h-[76px]';

function IglooColumn({
  iglooID,
  iglooName,
  ownedFamilies,       // [{ familyID, familyName, isPrimary, autoMode, position, isMember }]
  externalMembers,     // [{ familyID, familyName, isPending, isController }]
  memberCount,
  isController,
  maxExternalHeight,   // number — max external section height in px across all columns (for alignment)
  onPositionChange,    // (familyID, iglooID, newPosition) => void
  onAddToIgloo,        // (familyID, iglooID) => void
  onLeaveIgloo,        // (familyID, iglooID) => void
  canLeaveIgloo,       // (familyID, iglooID) => boolean
  onInviteByEmail,     // (iglooID, email, knownName) => void
  onCancelInvite,      // (iglooID, familyID) => void
  onRenameExternal,    // (externalFamilyID, newName) => void
  onRemoveExternal,    // (iglooID, familyID) => void
  onToggleController,  // (iglooID, familyID, makeController) => void
  onEditName,          // (iglooID, newName) => void
  onDelete,            // (iglooID) => void
  loadingCells,        // Set of "familyID:iglooID" keys currently loading
  color,               // { dot, dotHover, badge, badgeRing } from iglooColors palette
  isExternalIgloo,     // boolean — this igloo is external (we're not a controller)
}) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteKnownName, setInviteKnownName] = useState('');
  const [editingExternalID, setEditingExternalID] = useState(null);
  const [editedExternalName, setEditedExternalName] = useState('');
  const [hoveredExternalID, setHoveredExternalID] = useState(null);
  const [isInviting, setIsInviting] = useState(false);
  const [confirmRemoveExternal, setConfirmRemoveExternal] = useState(null);
  const [confirmLeave, setConfirmLeave] = useState(null);
  const [confirmToggleController, setConfirmToggleController] = useState(null); // { familyID, familyName, makeController }

  const canEdit = isController || ownedFamilies.some(f => f.isMember);

  const handleStartEditName = () => {
    setEditedName(iglooName);
    setIsEditingName(true);
  };

  const handleSaveName = () => {
    const trimmed = editedName.trim();
    if (trimmed && trimmed !== iglooName) {
      onEditName(iglooID, trimmed);
    }
    setIsEditingName(false);
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  const handleInvite = async () => {
    const trimmedEmail = inviteEmail.trim().toLowerCase();
    const trimmedName = inviteKnownName.trim();
    if (!trimmedEmail || !trimmedName) return;
    setIsInviting(true);
    try {
      await onInviteByEmail(iglooID, trimmedEmail, trimmedName);
      setInviteEmail('');
      setInviteKnownName('');
      setShowInviteModal(false);
    } finally {
      setIsInviting(false);
    }
  };

  const handleLeaveClick = (familyID) => {
    if (isExternalIgloo) {
      setConfirmLeave({ familyID });
    } else {
      onLeaveIgloo(familyID, iglooID);
    }
  };

  return (
    <div className="flex flex-col min-w-[120px] w-[120px] border border-slate-200 rounded-lg bg-white">
      {/* Header: igloo name + member count + color dot */}
      <div className="px-2 py-2 border-b border-slate-100 relative">
        {isEditingName ? (
          <div className="flex items-center gap-1 pr-0">
            <Input
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              className="text-xs font-semibold h-6 px-1 flex-1 min-w-0"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveName();
                if (e.key === 'Escape') handleCancelEditName();
              }}
              autoFocus
            />
            <button onClick={handleSaveName} className="text-green-600 hover:text-green-700 flex-shrink-0">
              <Check className="h-3.5 w-3.5" />
            </button>
            <button onClick={handleCancelEditName} className="text-red-500 hover:text-red-600 flex-shrink-0">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-1">
            <div
              className={`text-xs font-semibold text-slate-800 truncate ${canEdit ? 'cursor-pointer hover:underline' : ''}`}
              onClick={canEdit ? handleStartEditName : undefined}
              title={iglooName}
            >
              {iglooName}
            </div>
            {/* Color dot */}
            {color && (
              <div className={`w-3 h-3 rounded-full flex-shrink-0 mt-0.5 ${color.badge} ring-2 ${color.badgeRing}`} />
            )}
          </div>
        )}
        <div className="text-[10px] text-slate-400 mt-0.5">
          {memberCount} {memberCount === 1 ? 'member' : 'members'}
        </div>
      </div>

      {/* Owned families — TV if member (with x on hover to leave), "+" if not */}
      <div className="flex flex-col">
        {ownedFamilies.map((family) => {
          const cellKey = `${family.familyID}:${iglooID}`;
          const isLoading = loadingCells && loadingCells.has(cellKey);
          const canLeave = family.isMember && canLeaveIgloo(family.familyID, iglooID);

          return (
            <div key={family.familyID} className={`border-b border-slate-50 ${FAMILY_ROW_HEIGHT} flex items-center justify-center`}>
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-300 border-t-slate-600"></div>
              ) : family.isMember ? (
                <div className="relative group">
                  <PositionSelector
                    value={family.position || null}
                    onChange={(newPos) => onPositionChange(family.familyID, iglooID, newPos)}
                    color={color}
                    autoMode={family.autoMode}
                  />
                  {canLeave && (
                    <button
                      onClick={() => handleLeaveClick(family.familyID)}
                      className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white items-center justify-center transition-opacity hover:bg-red-600 shadow-sm hidden group-hover:flex"
                      title="Leave this Higlo"
                    >
                      <X className="h-3 w-3" strokeWidth={3} />
                    </button>
                  )}
                </div>
              ) : isController ? (
                <button
                  onClick={() => onAddToIgloo(family.familyID, iglooID)}
                  className="w-8 h-8 rounded-full border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 hover:border-slate-400 hover:text-slate-500 hover:bg-slate-50 transition-colors"
                  title={`Add ${family.familyName} to this Higlo`}
                >
                  <Plus className="h-4 w-4" />
                </button>
              ) : (
                <span className="text-[10px] text-slate-300">—</span>
              )}
            </div>
          );
        })}
      </div>

      {/* External members — or spacer for alignment if hasExternalMembers but this column has none */}
      {externalMembers.length > 0 ? (
        <>
          <Separator />
          <div className="py-1">
            {externalMembers.map((member) => {
              const isHovered = hoveredExternalID === member.familyID;
              const isEditing = editingExternalID === member.familyID;

              return (
                <div
                  key={member.familyID}
                  className={`relative px-1.5 py-1 ${member.isPending ? 'h-[42px]' : 'h-[28px]'} flex flex-col justify-center`}
                  onMouseEnter={() => setHoveredExternalID(member.familyID)}
                  onMouseLeave={() => setHoveredExternalID(null)}
                >
                  {isEditing ? (
                    <div className="flex items-center gap-0.5 w-full">
                      <Input
                        value={editedExternalName}
                        onChange={(e) => setEditedExternalName(e.target.value)}
                        className="text-xs h-6 px-1 flex-1 min-w-0"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const trimmed = editedExternalName.trim();
                            if (trimmed && trimmed !== member.familyName) {
                              onRenameExternal(member.familyID, trimmed);
                            }
                            setEditingExternalID(null);
                          }
                          if (e.key === 'Escape') setEditingExternalID(null);
                        }}
                        autoFocus
                      />
                      <button onClick={() => {
                        const trimmed = editedExternalName.trim();
                        if (trimmed && trimmed !== member.familyName) {
                          onRenameExternal(member.familyID, trimmed);
                        }
                        setEditingExternalID(null);
                      }} className="text-green-600 flex-shrink-0">
                        <Check className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => setEditingExternalID(null)} className="text-red-500 flex-shrink-0">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center w-full gap-0.5">
                      <span
                        className={`text-xs text-slate-600 truncate text-center ${canEdit ? 'cursor-pointer hover:underline' : ''}`}
                        title={canEdit ? `Click to rename ${member.familyName}` : member.familyName}
                        onClick={canEdit ? () => { setEditingExternalID(member.familyID); setEditedExternalName(member.familyName); } : undefined}
                      >
                        {member.familyName}
                      </span>
                      {/* Crown: gold if controller (clickable to demote), faint on hover to promote */}
                      {member.isController ? (
                        <button
                          onClick={isController ? () => setConfirmToggleController({ familyID: member.familyID, familyName: member.familyName, makeController: false }) : undefined}
                          className="flex-shrink-0"
                          title={isController ? `Remove ${member.familyName} as controller` : 'Controller'}
                          disabled={!isController}
                        >
                          <Crown className={`h-3 w-3 ${isController && isHovered ? 'text-red-400' : 'text-amber-500'} transition-colors`} />
                        </button>
                      ) : (
                        isController && !member.isPending && isHovered && (
                          <button
                            onClick={() => setConfirmToggleController({ familyID: member.familyID, familyName: member.familyName, makeController: true })}
                            className="flex-shrink-0"
                            title={`Make ${member.familyName} a controller`}
                          >
                            <Crown className="h-3 w-3 text-slate-300 hover:text-amber-500 transition-colors" />
                          </button>
                        )
                      )}
                      {/* Remove X at right edge — visible on hover */}
                      {isController && !member.isPending && isHovered && (
                        <button
                          onClick={() => setConfirmRemoveExternal({ familyID: member.familyID, familyName: member.familyName })}
                          className="absolute right-1 text-slate-300 hover:text-red-500 transition-colors flex-shrink-0"
                          title={`Remove ${member.familyName}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  )}
                  {member.isPending && (
                    <div className="flex items-center justify-center gap-1 mt-0.5">
                      <Badge variant="outline" className="text-[8px] px-1 py-0 h-3.5">
                        Pending
                      </Badge>
                      {isController && (
                        <button
                          onClick={() => onCancelInvite(iglooID, member.familyID)}
                          className="text-red-400 hover:text-red-600 transition-colors"
                          title="Cancel invite"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : maxExternalHeight > 0 ? (
        // Empty spacer to align with columns that have external members
        <div style={{ height: `${maxExternalHeight}px` }} />
      ) : null}

      {/* Invite + Delete/Leave buttons — always shown, greyed if not controller */}
      <div className="px-1.5 py-1.5 border-t border-slate-100 flex flex-col gap-1">
        {isController ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowInviteModal(true)}
            className="w-full h-6 text-[10px] text-slate-600 hover:text-slate-800 hover:bg-slate-50 px-1"
          >
            <UserPlus className="h-3 w-3 mr-0.5" />
            Invite
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            disabled
            className="w-full h-6 text-[10px] text-slate-300 px-1 cursor-not-allowed"
            title="You're not a controller of this Higlo and cannot invite others"
          >
            <UserPlus className="h-3 w-3 mr-0.5" />
            Invite
          </Button>
        )}
        {isController ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(iglooID)}
            className="w-full h-6 text-[10px] text-red-500 hover:text-red-600 hover:bg-red-50 px-1"
          >
            <Trash2 className="h-3 w-3 mr-0.5" />
            Delete
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const memberFamily = ownedFamilies.find(f => f.isMember);
              if (memberFamily) setConfirmLeave({ familyID: memberFamily.familyID });
            }}
            className="w-full h-6 text-[10px] text-red-500 hover:text-red-600 hover:bg-red-50 px-1"
          >
            <Trash2 className="h-3 w-3 mr-0.5" />
            Leave
          </Button>
        )}
      </div>

      {/* Invite modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Invite to "{iglooName}"</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Email address</label>
                <Input
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="name@example.com"
                  className="text-sm h-9"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Name you know them as</label>
                <Input
                  value={inviteKnownName}
                  onChange={(e) => setInviteKnownName(e.target.value)}
                  placeholder="e.g. The Smiths"
                  className="text-sm h-9"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && inviteEmail.trim() && inviteKnownName.trim()) handleInvite();
                  }}
                />
              </div>
              <div className="flex gap-2 justify-end pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowInviteModal(false); setInviteEmail(''); setInviteKnownName(''); }}
                  disabled={isInviting}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="bg-slate-800 text-white hover:bg-slate-700"
                  onClick={handleInvite}
                  disabled={!inviteEmail.trim() || !inviteKnownName.trim() || isInviting}
                >
                  {isInviting ? 'Sending...' : 'Send Invite'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirm remove external member */}
      {confirmRemoveExternal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Remove from "{iglooName}"</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-3">
                Are you sure you want to remove <strong>{confirmRemoveExternal.familyName}</strong> from this Higlo? They will no longer be able to see or join this space.
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setConfirmRemoveExternal(null)}>
                  Cancel
                </Button>
                <Button
                  variant="default" size="sm"
                  className="bg-red-600 text-white hover:bg-red-700"
                  onClick={() => {
                    onRemoveExternal(iglooID, confirmRemoveExternal.familyID);
                    setConfirmRemoveExternal(null);
                  }}
                >
                  Remove
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirm leave external igloo */}
      {confirmLeave && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Leave "{iglooName}"</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-3">
                Are you sure you want to leave this Higlo? You won't be able to rejoin unless you're invited again.
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setConfirmLeave(null)}>
                  Cancel
                </Button>
                <Button
                  variant="default" size="sm"
                  className="bg-red-600 text-white hover:bg-red-700"
                  onClick={() => {
                    onLeaveIgloo(confirmLeave.familyID, iglooID);
                    setConfirmLeave(null);
                  }}
                >
                  Leave
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirm promote/demote controller */}
      {confirmToggleController && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {confirmToggleController.makeController ? 'Make Controller' : 'Remove as Controller'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-3">
                {confirmToggleController.makeController
                  ? <>Make <strong>{confirmToggleController.familyName}</strong> a controller of "{iglooName}"? Controllers can invite and remove other members.</>
                  : <>Remove <strong>{confirmToggleController.familyName}</strong> as a controller of "{iglooName}"? They will remain a member but can no longer invite or remove others.</>
                }
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setConfirmToggleController(null)}>
                  Cancel
                </Button>
                <Button
                  variant="default" size="sm"
                  className="bg-slate-800 text-white hover:bg-slate-700"
                  onClick={() => {
                    onToggleController(iglooID, confirmToggleController.familyID, confirmToggleController.makeController);
                    setConfirmToggleController(null);
                  }}
                >
                  {confirmToggleController.makeController ? 'Make Controller' : 'Remove as Controller'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default IglooColumn;
